import { action, observable, makeObservable } from 'mobx';
import { BaseViewStore } from 'core/stores';
import { OrderStore } from 'modules/order/stores';
import { OrderLineStore } from 'modules/order-line/stores';
import { notificationError, formatDates, isSuperAdminOrganization } from 'core/utils';
import {
	orderStatus,
	orderLineStatusesForSplit,
	sourceOfChange,
	fulfillmentOrderStatus,
	channelType,
} from 'common/constants';
import { ModalParams, ConfirmModalParams } from 'core/models';
import _ from 'lodash';

class MarketplaceOrderDetailViewStore extends BaseViewStore {
	@observable data = [];
	@observable refresh = false;
	@observable orderLines = null;
	@observable fulfillmentOrders = null;
	@observable isReadyForSplitting = false;
	@observable shipmentInfoReturns = [];
	itemPrice = 0;
	showActions = false;
	hasPiiAccess = false;
	isBuyerRequestedCancel = false;
	order = null;
	showSplitOption;

	historyLogModal = new ModalParams({
		onClose: this.closeHistoryLogModal,
	});

	@action.bound
	openCancelAllOrderLinesConfirmModal = () => {
		this.cancelAllOrderLinesConfirmModal.open({
			message: ['ORDERVIEW.CANCELALLORDERLINESCONFIRM'],
		});
	};

	cancelAllOrderLinesConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.cancelAllOrderLines();
		},
	});

	@action.bound
	openRefundAllOrderLinesConfirmModal = () => {
		this.refundAllOrderLinesConfirmModal.open({
			message: ['ORDERVIEW.REFUNDALLORDERLINESCONFIRM'],
		});
	};

	refundAllOrderLinesConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			await this.refundAllOrderLines();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.hasPiiAccess = this.rootStore.authStore.hasPiiAccess;
		this.listStore = new OrderStore(rootStore);
		this.orderLineStore = new OrderLineStore(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound async initialize() {
		this.suspend();
		await this.getOrder(this.rootStore.routerStore.routerState.params.id, {
			embed: [
				'shippingCoreCountry',
				'shippingCoreCountryState',
				'fulfillmentOrders',
				'fulfillmentOrders.fulfillmentOrderStatus',
				'fulfillmentOrders.carrierService',
				'fulfillmentOrders.onHoldReason',
				'fulfillmentOrders.warehouseOrganization',
				'fulfillmentOrders.shipmentInfos.shipmentInfoStatus',
				'fulfillmentOrders.shipmentInfos',
				'shipmentInfoReturns',
				'shipmentInfoReturns.returnedItems',
				'shipmentInfoReturns.returnedItems.product',
				'shipmentInfoReturns.carrierService',
				'carrier',
				'channel',
				'channel.timezone',
				'channel.channelType',
				'confirmationType',
				'currency',
				'seller',
				'seller.sellerOrganizationInfo',
				'orderStatus',
				'orderType',
				'orderLines',
				'orderLines.product',
				'orderLines.product.productUpcs',
				'orderLines.orderLineStatus',
				'shippingCoreCountry',
				'shippingCoreCountryState',
			],
		});
		this.resume();
	}

	@action.bound
	getOrder = async (id, options) => {
		await this.listStore.getOrder(id, options);
		this.order = this.listStore.item;
		this.orderLines = this.order.orderLines;
		this.fulfillmentOrders = this.order.fulfillmentOrders;
		this.shipmentInfoReturns = this.order.shipmentInfoReturns;
		this.isReadyForSplitting =
			this.order.orderStatus.abrv === orderStatus.unshipped ||
			this.order.orderStatus.abrv === orderStatus.partiallyShipped;

		if (
			!this.order.seller.sellerOrganizationInfo.partiallyFulfillMixedOrders &&
			this.orderLines.some(ol => ol.product != null && !ol.product.isActive) &&
			this.fulfillmentOrders.length == 0
		) {
			this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list');
			this.rootStore.notificationStore.warning([
				'ORDERVIEW.PARTIALLYFULFILLMIXEDORDERS',
				{ orderId: this.order.orderNumber },
			]);
			this.resume();
			return;
		}

		formatDates(this.listStore.item, ['orderDate', 'paymentDate'], true, 'LLL');
		formatDates(this.listStore.item, ['shipByDate', 'deliverByDate'], false, 'L');
		this.hasUnlinkedOrderLines =
			this.orderLines &&
			this.orderLines.length != 0 &&
			_.find(this.orderLines, item => {
				return item.product == null;
			});

		// Some of conditions for rendering cancel and refund buttons.
		if (
			this.order.channel.channelType.name == channelType.amazon ||
			this.order.channel.channelType.name == channelType.mirakl
		) {
			this.isBuyerRequestedCancel =
				this.orderLines != null && this.orderLines.length != 0
					? _.some(this.orderLines, item => {
							return item.isBuyerRequestedCancel == true;
					  })
					: false; // Cancelable if some of order lines are buyer requested to cancel.
			this.hasNoShipmentInfos = _.every(this.fulfillmentOrders, item => {
				return item.shipmentInfos == null || item.shipmentInfos.length == 0;
			}); // Cancelable if none of FOs have label.

			this.hasAllFOsPastExitScan =
				this.fulfillmentOrders != null && this.fulfillmentOrders.length != 0
					? _.every(this.fulfillmentOrders, item => {
							return (
								item.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan ||
								item.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.replaced ||
								item.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.notReceived ||
								item.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.returned
							);
					  })
					: false; // Refundable if all statuses for all FOs are past Exit Scan.
		}
		this.hasState = this.order.shippingCoreCountryState != undefined ? true : false;
		this.hasFulfillmentOrders = this.fulfillmentOrders && this.fulfillmentOrders.length != 0 ? true : false;
		this.showSplitOption = this.listStore.item.orderLines.some(ol => {
			return orderLineStatusesForSplit.includes(ol.orderLineStatus.abrv);
		});

		this.calculateItemPrice();
	};

	@action.bound
	cancelAllOrderLines = async () => {
		this.suspend();
		try {
			const {
				data: { total, canceled, refunded, message },
			} =
				this.order.channel.channelType.name == channelType.amazon
					? await this.orderLineStore.cancelAmazonOrder(this.order.id, sourceOfChange.cancelAllOrderLines)
					: await this.orderLineStore.cancelMiraklOrder(this.order.id, sourceOfChange.cancelAllOrderLines);
			if (message) {
				this.rootStore.notificationStore.error(message); //// CANCELEDORDERTOOLATE
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.CANCELORDERSSUCCESS',
					{ total, canceled, refunded, failed: total - (canceled + refunded) },
				]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	refundAllOrderLines = async () => {
		this.suspend();
		try {
			const {
				data: { total, canceled, refunded, message },
			} = await this.orderLineStore.refundAllOrderLines(this.order.id, sourceOfChange.cancelAllOrderLines);
			if (message) {
				this.rootStore.notificationStore.error(message);
			} else {
				this.rootStore.notificationStore.success([
					'ORDERVIEW.REFUNDORDERLINESSUCCESS',
					{ total, refunded, failed: total - (canceled + refunded) },
				]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	calculateItemPrice() {
		let itemPrice = 0;
		_.each(this.order.orderLines, orderLine => {
			itemPrice += orderLine.unitPrice * orderLine.quantity;
		});
		this.itemPrice = itemPrice;
	}

	@action.bound
	splitOrder = async () => {
		this.showSplitOption = false;
		this.suspend();
		try {
			var response = await this.listStore.splitMarketplaceOrder(
				this.rootStore.routerStore.routerState.params.id,
				sourceOfChange.splitOrder
			);
			if (response[0].isSuccess) {
				response[0].inactiveOrderLineIds.length === 0
					? this.rootStore.notificationStore.success(['ORDERVIEW.SPLITMARKETPLACEORDERSUCCESS'])
					: this.rootStore.notificationStore.warning([
							'ORDERVIEW.SPLITMARKETPLACEORDERPARTIALSUCCESS',
							{
								successful: this.orderLines.length - response[0].inactiveOrderLineIds.length,
								total: this.orderLines.length,
							},
					  ]);
			} else {
				this.rootStore.notificationStore.error([
					response[0].message ? response[0].message : 'ORDERVIEW.ORDERINQUEUEFORSPLIT',
				]);
			}
			this.initialize();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
			this.initialize();
		}
		this.resume();
	};

	goBack = () => {
		this.rootStore.routerStore.goTo('master.app.main.order.marketplace.list', {});
	};

	refreshPage = () => {
		this.initialize();
	};

	@action.bound
	openReplacementModal = async () => {
		this.replacementModal.open();
	};

	@action.bound
	async openHistoryLogModal() {
		this.historyLogModal.open();
	}

	@action.bound
	async closeHistoryLogModal() {
		this.historyLogModal.close();
	}

	replacementModal = new ModalParams();
}

export default MarketplaceOrderDetailViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/MarketplaceOrderDetailViewStore.js